import { FLOURISH_DURATION_MS, JUMP_FLOURISH } from "./animation_pose"
import type { Entity } from "./models"

/** Continuous running needed to vault a wall; jumping onto a single block does not need a run-up. */
export const WALL_VAULT_RUN_UP_MS = 250
const RUN_UP_MAX_GAP_MS = 200
/** Time to press jump after a running approach meets the wall. */
const WALL_CONTACT_GRACE_MS = 500

/** Whether a jump now clears a wall: run up for long enough, and recently (each give or take `slackMs`). */
export const canVaultWall = (entity: Entity, now: number, slackMs = 0): boolean =>
    !!entity.boosted && entity.runningSinceTs !== undefined && entity.lastRunningMoveTs !== undefined &&
    now - entity.lastRunningMoveTs <= WALL_CONTACT_GRACE_MS + slackMs &&
    entity.lastRunningMoveTs - entity.runningSinceTs >= WALL_VAULT_RUN_UP_MS - slackMs

/** Called by both sides after actual movement, so pushing against a wall cannot build a run-up. */
export const updateJumpRunUp = (entity: Entity, moved: boolean, now: number, runningIntent = moved): void => {
    const predicted = entity.predictedFlourish
    const jumping = (entity.jump && !entity.jump.landed && now - entity.jump.startedAt < JUMP_LANDING_MS) ||
        (predicted?.view.action === JUMP_FLOURISH && now - predicted.at < JUMP_LANDING_MS)
    if (!entity.boosted || !runningIntent || jumping || entity.floorFall || entity.stairTravel ||
        (!moved && (entity.lastRunningMoveTs === undefined || now - entity.lastRunningMoveTs > WALL_CONTACT_GRACE_MS))) {
        entity.runningSinceTs = undefined
        entity.lastRunningMoveTs = undefined
        return
    }
    // Brief contact with the wall must not erase an earned run-up before Space
    // arrives. Blocked time adds nothing, and holding against the wall expires it.
    if (!moved) return
    if (entity.lastRunningMoveTs === undefined || now - entity.lastRunningMoveTs > RUN_UP_MAX_GAP_MS)
        entity.runningSinceTs = now
    entity.lastRunningMoveTs = now
}

/**
 * A player's jump (the Space key), as client and server both reckon it. The body is drawn lifted on a
 * gravity curve (client effects/body_flourish.ts) while its feet are off the ground; it carries the
 * speed it took off with, steering nothing until it is down; and the floor it is over does not let
 * it fall until it comes down -- so a running jump clears a gap, and one off a storey's edge carries
 * on into the drop (server campaigns/default/default.ts handleFloorFall) on the same curve.
 */
export const JUMP_MS = FLOURISH_DURATION_MS[JUMP_FLOURISH]

/** The peak, in world pixels above the ground. */
export const JUMP_HEIGHT = 14

/** Extra visual height at the peak of a running jump; airtime and movement stay the same. */
export const RUNNING_JUMP_EXTRA_HEIGHT = 16

/**
 * When the soles leave the ground and are back on it, as shares of JUMP_MS: after the dip and
 * before the landing is taken in the knees (ember_sprite/clips.ts JUMP_KEYS, whose times these are).
 */
export const JUMP_TAKEOFF = 0.16
export const JUMP_LANDING = 0.85
/** The clip's legs-reaching-down frame (its 9th of 12), which a leap off an edge holds all the way down. */
export const JUMP_HOLD = 8 / 11
export const JUMP_TAKEOFF_MS = JUMP_TAKEOFF * JUMP_MS
export const JUMP_LANDING_MS = JUMP_LANDING * JUMP_MS
const AIR_MS = JUMP_LANDING_MS - JUMP_TAKEOFF_MS

/** The jump's gravity, px/ms²: what brings JUMP_HEIGHT back down in the time it is in the air. */
export const JUMP_GRAVITY = (8 * JUMP_HEIGHT) / (AIR_MS * AIR_MS)

/** How much faster than its run-up a body travels through the air: a leap, not a hop. */
export const JUMP_CARRY = 1.2

/** How long after coming down over open floor a jump still turns into a leaping fall. */
export const LEAP_GRACE_MS = 300

/** How high the body is `elapsedMs` into its jump. */
export const jumpLift = (elapsedMs: number): number => {
    const u = (elapsedMs - JUMP_TAKEOFF_MS) / AIR_MS
    return u <= 0 || u >= 1 ? 0 : JUMP_HEIGHT * 4 * u * (1 - u)
}

/** The visible running-jump arc, shared by rendering and wall clearance. */
export const runningJumpLift = (elapsedMs: number): number =>
    jumpLift(elapsedMs) * (JUMP_HEIGHT + RUNNING_JUMP_EXTRA_HEIGHT) / JUMP_HEIGHT

/** Whether a jump that began at `startedAt` has its feet off the ground at `now`. */
export const jumpAirborne = (startedAt: number, now: number): boolean => now - startedAt < JUMP_LANDING_MS

/**
 * The fall a leap off an edge carries on into, `distance` px down: the jump's own gravity, picked
 * up as the body comes back level with the floor it left -- already JUMP_HEIGHT fallen from the top
 * of the jump, and moving as fast as that has made it -- so the curve does not stop and start again.
 */
export const leapFallTrajectory = (distance: number, totalDistance = distance) => {
    const startElapsedMs = AIR_MS / 2
    const endElapsedMs = Math.sqrt((2 * (JUMP_HEIGHT + distance)) / JUMP_GRAVITY)
    return {
        durationMs: endElapsedMs - startElapsedMs,
        trajectory: { gravity: JUMP_GRAVITY, distanceBefore: JUMP_HEIGHT, endElapsedMs, totalDistance: JUMP_HEIGHT + totalDistance },
    }
}

/**
 * How long after a jump up onto a wall comes down its client waits for the server to have moved the body
 * up there (a round trip, and then some): the body is held still and drawn on the top meanwhile.
 */
export const MOUNT_WAIT_MS = 600

/** How far over a wall's top a jump up onto it peaks, world pixels. */
export const MOUNT_CLEARANCE = 8

/**
 * Where a jump up onto a wall has the drawn body `elapsedMs` in, as an offset from where it took off
 * (up is negative): still in the dip, then off the ground on one parabola that clears the top by
 * MOUNT_CLEARANCE and comes down `dx` across and `dy` up at the landing, held there after.
 */
export const mountOffset = (elapsedMs: number, dx: number, dy: number): { x: number; y: number } => {
    const u = Math.max(0, Math.min(1, (elapsedMs - JUMP_TAKEOFF_MS) / AIR_MS))
    // Height above the take-off point: 0 at u = 0, `rise` at u = 1, a peak of `peak` between.
    const rise = -dy, peak = Math.max(rise, 0) + MOUNT_CLEARANCE
    const b = 2 * peak + 2 * Math.sqrt(peak * (peak - rise))
    const a = rise - b
    return { x: dx * u, y: -(a * u * u + b * u) }
}
